import { useEffect, useState } from 'react';
import { api } from '../api';
import { shortDate } from '../format';
import type { LeaseDetail, LeaseDocument, SignatureEnvelope, SignatureEvent } from '../types';

const STATUS_LABELS: Record<SignatureEnvelope['status'], string> = {
  SENT: 'Envoyée', OPENED: 'Ouverte', IDENTIFIED: 'Identifié',
  SIGNED: 'Signée', EXPIRED: 'Expirée', CANCELLED: 'Annulée',
};

const DOCUMENT_CHOICES = [
  { role: 'BAIL', label: 'Contrat de colocation' },
  { role: 'CHARTE', label: 'Charte de colocation' },
  { role: 'ATTESTATION', label: 'Attestation de versement' },
];

/** Qui peut signer, et à quel titre. Le garant n'y figure pas : son acte se signe à la main. */
function signerChoices(lease: LeaseDetail): { id: string; label: string; role: 'TENANT' | 'LEGAL_REPRESENTATIVE' | 'PAYER' }[] {
  const choices: { id: string; label: string; role: 'TENANT' | 'LEGAL_REPRESENTATIVE' | 'PAYER' }[] = [];
  if (lease.representative) {
    // Un locataire mineur ne signe pas : c'est son représentant légal qui engage.
    choices.push({
      id: lease.representative.id,
      label: `${lease.representative.firstName} ${lease.representative.lastName} (représentant légal)`,
      role: 'LEGAL_REPRESENTATIVE',
    });
  } else {
    choices.push({
      id: lease.tenant.id,
      label: `${lease.tenant.firstName} ${lease.tenant.lastName} (locataire)`,
      role: 'TENANT',
    });
  }
  if (lease.payer) {
    choices.push({
      id: lease.payer.id,
      label: `${lease.payer.firstName} ${lease.payer.lastName} (tiers payeur)`,
      role: 'PAYER',
    });
  }
  return choices;
}

export function LeaseSignature({ lease, onChanged }: { lease: LeaseDetail; onChanged: () => void }) {
  const [envelopes, setEnvelopes] = useState<SignatureEnvelope[] | null>(null);
  // Les documents doivent exister avant d'être envoyés à signature : l'écran
  // le dit désormais, au lieu de laisser partir une requête vouée à échouer.
  const [available, setAvailable] = useState<Set<string>>(new Set());
  const [events, setEvents] = useState<Record<string, SignatureEvent[]>>({});
  const [roles, setRoles] = useState<string[]>(['BAIL', 'CHARTE', 'ATTESTATION']);
  const [signerId, setSignerId] = useState('');
  const [message, setMessage] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const choices = signerChoices(lease);
  const selectable = roles.filter((role) => available.has(role));

  async function load(): Promise<void> {
    try {
      const [result, documents] = await Promise.all([
        api<{ envelopes: SignatureEnvelope[] }>(`/api/admin/leases/${lease.id}/signature-envelopes`),
        api<{ documents: LeaseDocument[] }>(`/api/admin/leases/${lease.id}/documents`),
      ]);
      setEnvelopes(result.envelopes);
      setAvailable(new Set(
        documents.documents.filter((document) => !document.signed && document.role).map((document) => document.role!),
      ));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    setNotice('');
    setError('');
    setSignerId(choices[0]?.id ?? '');
    void load();
  }, [lease.id]);

  async function create(): Promise<void> {
    const choice = choices.find((entry) => entry.id === signerId);
    if (!choice) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/leases/${lease.id}/signature-envelopes`, {
        method: 'POST',
        body: JSON.stringify({
          roles: selectable,
          signerPersonId: choice.id,
          signerRole: choice.role,
          ...(message.trim() ? { message: message.trim() } : {}),
        }),
      });
      setNotice(`Demande envoyée à ${choice.label}.`);
      setMessage('');
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id: string): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/signature-envelopes/${id}/cancel`, { method: 'POST', body: JSON.stringify({}) });
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Annulation impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function loadEvents(id: string): Promise<void> {
    try {
      const result = await api<{ events: SignatureEvent[] }>(`/api/admin/signature-envelopes/${id}/events`);
      setEvents((current) => ({ ...current, [id]: result.events }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Journal indisponible.');
    }
  }

  async function sendHandwritten(): Promise<void> {
    const guarantor = lease.guarantee?.guarantor;
    if (!guarantor) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/leases/${lease.id}/handwritten`, {
        method: 'POST',
        body: JSON.stringify({ role: 'CAUTIONNEMENT', recipientPersonId: guarantor.id }),
      });
      setNotice(`Acte de cautionnement envoyé à ${guarantor.firstName} ${guarantor.lastName}, à signer à la main.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="lease-signature">
      <div className="section-heading">
        <div><p className="eyebrow">Signature</p><h3>Demandes de signature</h3></div>
      </div>
      <p className="section-intro">
        Une demande = un signataire = les documents qu'il doit signer. Le signataire confirme sa date de
        naissance, reçoit un code par e-mail, lit chaque document en entier, puis signe. Un certificat de
        preuve est produit et transmis aux deux parties.
      </p>

      <fieldset className="config-group">
        <legend>Nouvelle demande</legend>
        <div className="form-grid">
          <label>Signataire
            <select value={signerId} onChange={(event) => setSignerId(event.target.value)}>
              {choices.map((choice) => <option key={choice.id} value={choice.id}>{choice.label}</option>)}
            </select>
          </label>
          <label>Mot d'accompagnement (facultatif)
            <input type="text" maxLength={800} value={message} onChange={(event) => setMessage(event.target.value)} />
          </label>
        </div>
        <div className="signature-doc-picks">
          {DOCUMENT_CHOICES.map((document) => {
            const ready = available.has(document.role);
            return (
              <label className={ready ? 'allocation-pick' : 'allocation-pick disabled'} key={document.role}>
                <input
                  type="checkbox"
                  disabled={!ready}
                  checked={ready && roles.includes(document.role)}
                  onChange={(event) => setRoles((current) => (event.target.checked
                    ? [...current, document.role]
                    : current.filter((role) => role !== document.role)))}
                />
                <span>
                  {document.label}
                  {!ready && <small>Pas encore produit : cliquez « Produire les documents » ci-dessus.</small>}
                </span>
              </label>
            );
          })}
        </div>
        <div className="record-actions">
          <button
            type="button"
            className="button primary compact"
            disabled={busy || selectable.length === 0 || !signerId}
            title={selectable.length === 0 ? 'Produisez d’abord les documents à signer.' : undefined}
            onClick={() => void create()}
          >
            Envoyer à signer
          </button>
        </div>
      </fieldset>

      {lease.guarantee?.type === 'GUARANTOR' && lease.guarantee.guarantor && (
        <div className="deposit-note">
          <strong>Acte de cautionnement : signature manuscrite obligatoire</strong>
          <span>
            L'article 2297 du Code civil impose que la caution écrive elle-même la mention d'engagement, à
            peine de nullité. Une mention tapée au clavier ne vaut pas : cet acte ne passe donc jamais par la
            signature en ligne. Il part par e-mail, à imprimer, compléter à la main et retourner scanné.
          </span>
          <div className="record-actions">
            <button type="button" className="button secondary compact" disabled={busy} onClick={() => void sendHandwritten()}>
              Envoyer l'acte à {lease.guarantee.guarantor.firstName} {lease.guarantee.guarantor.lastName}
            </button>
          </div>
        </div>
      )}

      {notice && <div className="inline-message success">{notice}</div>}
      {error && <div className="inline-message error" role="alert">{error}</div>}

      {envelopes?.length === 0 && <div className="empty-state">Aucune demande de signature pour ce bail.</div>}
      <div className="record-list">
        {envelopes?.map((envelope) => (
          <article className="record-card" key={envelope.id}>
            <div className="record-heading">
              <div>
                <strong>{envelope.signerName}</strong>
                <span className="record-code">{envelope.documents.join(' · ')}</span>
              </div>
              <span className={`status ${envelope.status.toLowerCase()}`}>{STATUS_LABELS[envelope.status]}</span>
            </div>
            <p className="record-line muted">
              <span>
                envoyée le {shortDate(envelope.createdAt.slice(0, 10))} · expire le {shortDate(envelope.expiresAt.slice(0, 10))}
                {envelope.signedAt && ` · signée le ${shortDate(envelope.signedAt.slice(0, 10))}`}
              </span>
              <span>{envelope.signerEmail}</span>
            </p>
            <div className="record-actions">
              {envelope.certificateDocumentId && (
                <a className="button secondary compact" href={`/api/files/${envelope.certificateDocumentId}`} target="_blank" rel="noreferrer">
                  Certificat de preuve
                </a>
              )}
              <button type="button" className="text-button" onClick={() => void loadEvents(envelope.id)}>
                Journal
              </button>
              {envelope.status !== 'SIGNED' && envelope.status !== 'CANCELLED' && (
                <button type="button" className="button secondary compact" disabled={busy} onClick={() => void cancel(envelope.id)}>
                  Annuler
                </button>
              )}
            </div>
            {events[envelope.id] && (
              <ol className="signature-journal">
                {events[envelope.id]!.map((event, index) => (
                  <li key={`${event.occurredAt}-${index}`}>
                    <time>{new Date(event.occurredAt).toLocaleString('fr-FR')}</time>
                    <strong>{event.type}</strong>
                    {event.detail && <span>{event.detail}</span>}
                  </li>
                ))}
              </ol>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
